import { Modal } from 'react-bootstrap';
import { TYPES_TO_NAMES } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { Button } from '../../Components/Button';
import { Appearance, Design, Size } from '../../Components/designConstants';
import { FormattedOrderRow } from './linear-orders-constants';
import './OrderHistoryModal.scss';

interface OrderHistoryModalProps {
  orderInfo: FormattedOrderRow;
  downloadPDF: (id: number, path: string, isV1?: boolean) => Promise<void>;
  onClose: () => void;
}

// TODO: The below functions also live in bpm-lambdas. Now that I'm using here too, should move them to bpm-types
/**
 * Returns the order version, based on 1) whether it's order or traffic and 2) its revision number.
 */
const getOrderVersion = (orderInfo: FormattedOrderRow, revisionNumber: number) => {
  const { is_traffic, is_blanket } = orderInfo;
  if (is_blanket) {
    return revisionNumber === 1
      ? 'New Blanket Traffic Instructions'
      : 'Revised Blanket Traffic Instructions';
  }
  if (is_traffic) {
    return revisionNumber === 1 ? 'New Traffic Instructions' : 'Revised Traffic Instructions';
  }
  return revisionNumber === 1 ? 'New Order' : 'Revised Order';
};

/**
 * Returns the order-type label used in V1 (old buying page) S3 paths.
 */
const getV1OrderType = (isTraffic: boolean, type: number) => {
  if (isTraffic) {
    return 'Traffic';
  }
  return type === 1 ? 'Precommitted' : 'Regular';
};

/**
 * Creates the S3 path for the order PDF.
 *
 * i.e. "production/123/123-1_ABC_N_New Order_Regular.pdf"
 */
const makeS3Path = (orderInfo: FormattedOrderRow, revisionNumber: number) => {
  const { id, network, avail, type } = orderInfo;
  const orderVersion = getOrderVersion(orderInfo, revisionNumber);
  const orderType = TYPES_TO_NAMES[`${type}`];

  const path = `production/${id}/${id}-${revisionNumber}_${network}_${avail}_${orderVersion}_${orderType}.pdf`;
  return path;
};

/**
 * Creates the S3 path for orders that were made on the old buying page.
 *
 * i.e. "production/123/456-1_ABC_N_New Order_Regular.pdf"
 */
const makeS3PathV1 = (orderInfo: FormattedOrderRow, revisionNumber: number) => {
  const { id, order_id, network, avail, type, is_traffic, is_blanket } = orderInfo;
  const orderVersion = getOrderVersion(orderInfo, revisionNumber);
  const orderType = is_blanket ? 'Blanket Traffic' : getV1OrderType(is_traffic, type);

  const path = `production/${id}/${order_id}-${revisionNumber}_${network}_${avail}_${orderVersion}_${orderType}.pdf`;
  return path;
};

/**
 * Creates the S3 path for the order PDF.
 *
 * i.e. "production/123/123-1_ABC_N_New Order_Regular.pdf"
 */
const makeConfirmedS3Path = (orderInfo: FormattedOrderRow, revisionNumber: number) => {
  const { id, network, avail } = orderInfo;
  const path = `production/${id}/${id}-${revisionNumber}_${network}_${avail}_Approved.pdf`;
  return path;
};

export const OrderHistoryModal = ({ orderInfo, downloadPDF, onClose }: OrderHistoryModalProps) => {
  const { id, isV1, revision, is_traffic } = orderInfo;
  const revisionNumbers = Array.from({ length: revision }, (_, index) => index + 1);
  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>PDF Revision History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='order-list'>
          <div>Sent</div>
          {revisionNumbers.map(revisionNumber => {
            const currentOrderNumber = `${id}-${revisionNumber}`;
            const currentS3Path = isV1
              ? makeS3PathV1(orderInfo, revisionNumber)
              : makeS3Path(orderInfo, revisionNumber);
            return (
              <Button
                size={Size.LG}
                key={currentOrderNumber}
                appearance={Appearance.EMPTY}
                className='pdf-link'
                onClick={() => downloadPDF(id, currentS3Path, isV1)}
              >
                {currentOrderNumber}
              </Button>
            );
          })}
          {!is_traffic && (
            <>
              <div>Confirmed</div>
              {revisionNumbers.map(revisionNumber => {
                const currentOrderNumber = `${id}-${revisionNumber}`;
                const currentS3Path = makeConfirmedS3Path(orderInfo, revisionNumber);
                return (
                  <Button
                    size={Size.LG}
                    key={currentOrderNumber}
                    appearance={Appearance.EMPTY}
                    className='pdf-link'
                    onClick={() => downloadPDF(id, currentS3Path, true)}
                  >
                    {currentOrderNumber}
                  </Button>
                );
              })}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
